/**
 * KYC Restrictions Utility
 * 
 * Helper functions to check if actions are restricted based on user KYC status
 */

/**
 * Check if an action is restricted for the current user
 * @param {Array} kycRestrictions - Array of active restrictions from appLayout
 * @param {string} actionKey - The action to check (e.g., 'payout', 'balance_transfer')
 * @param {number} userKycStatus - The user's current KYC status
 * @returns {Object} { isRestricted: boolean, message?: string }
 */
export const isActionRestricted = (kycRestrictions, actionKey, userKycStatus) => {
    if (!kycRestrictions || !Array.isArray(kycRestrictions)) {
        return { isRestricted: false };
    }

    const restriction = kycRestrictions.find(r => r.actionKey === actionKey);

    if (!restriction || !restriction.isRestricted) {
        return { isRestricted: false };
    }

    // User is restricted if their KYC status doesn't match required
    if (userKycStatus !== restriction.requiredKycStatus) {
        return {
            isRestricted: true,
            message: restriction.restrictionMessage || 'This action requires KYC approval.',
            requiredKycStatus: restriction.requiredKycStatus,
            actionName: restriction.actionName,
        };
    }

    return { isRestricted: false };
};

/**
 * Get the user's KYC status from appLayout data
 * @param {Object} appLayoutData - The appLayout object from redux (state.dashboard.appLayout)
 * @returns {number} KYC status (0 = pending, 1 = approved, 2 = rejected)
 */
export const getUserKycStatus = (appLayoutData) => {
    // kycStatus is returned in user object from appLayout API
    return appLayoutData?.user?.kycStatus ?? 0;
};

/**
 * Map of action keys to their display names and icons
 */
export const ACTION_MAP = {
    payout: { name: 'Money Payout', icon: 'fa-money-bill-wave' },
    balance_transfer: { name: 'Balance Transfer', icon: 'fa-exchange-alt' },
    epin_transfer: { name: 'E-pin Transfer', icon: 'fa-key' },
    ecard_purchase: { name: 'E-Card Purchase', icon: 'fa-credit-card' },
    ecard_request: { name: 'E-Card Request', icon: 'fa-file-alt' },
    account_renewal: { name: 'Account Renewal', icon: 'fa-sync' },
    shopping: { name: 'Shopping', icon: 'fa-shopping-cart' },
    ewallet: { name: 'E-wallet', icon: 'fa-wallet' },
};

/**
 * Check multiple actions at once
 * @param {Array} kycRestrictions - Array of active restrictions
 * @param {Array} actionKeys - Array of action keys to check
 * @param {number} userKycStatus - The user's current KYC status
 * @returns {Object} Map of actionKey -> restriction result
 */
export const checkMultipleActions = (kycRestrictions, actionKeys, userKycStatus) => {
    const result = {};
    for (const actionKey of actionKeys) {
        result[actionKey] = isActionRestricted(kycRestrictions, actionKey, userKycStatus);
    }
    return result;
};

export default {
    isActionRestricted,
    getUserKycStatus,
    checkMultipleActions,
    ACTION_MAP,
};
